// Client helper for server-side signed Avalanche writes.
//
// SECURITY: the signing PRIVATE_KEY never reaches the browser. On-chain
// writes are POSTed to the orchestrator (/avalanche/write), which requires a
// fresh personal_sign proof from the user's connected wallet, checks the
// address against the on-chain user whitelist server-side, then signs the
// transaction with the server-held key. Reads remain client-side via the
// public RPC endpoint.
import { getOrchestratorBaseUrl } from "../magic-link/guestSession";
// Registered by the wallet provider layer (AvalancheProvider) once a wallet
// is connected; writes are impossible without a connected wallet.
let authSigner = null;
export function setAvalancheWriteAuthSigner(signer) {
    authSigner = signer;
}
/** Must match the server (upload-orchestrator/src/routes/avalanche.js). */
function buildWriteAuthMessage(address, timestamp, contract, method, args) {
    return `dwinity-avalanche-write\n${address.toLowerCase()}\n${timestamp}\n${contract}.${method}\n${JSON.stringify(args)}`;
}
/** Must match the server (upload-orchestrator/src/routes/avalanche.js). */
function buildDecryptAuthMessage(address, timestamp, items) {
    return `dwinity-avalanche-decrypt\n${address.toLowerCase()}\n${timestamp}\n${JSON.stringify(items)}`;
}
export async function avalancheServerWrite(contract, method, args) {
    const address = authSigner?.getAddress() ?? null;
    if (!authSigner || !address) {
        throw new Error(`Avalanche write ${method} failed: no connected wallet to authorize the request`);
    }
    const timestamp = Date.now();
    const message = buildWriteAuthMessage(address, timestamp, contract, method, args);
    const signature = await authSigner.signMessage(message, address);
    const res = await fetch(`${getOrchestratorBaseUrl()}/avalanche/write`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract, method, args, auth: { address, timestamp, signature } }),
    });
    if (!res.ok) {
        let reason = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error)
                reason = body.error;
        }
        catch {
            /* ignore body parse errors */
        }
        throw new Error(`Avalanche write ${method} failed: ${reason}`);
    }
    return res.json();
}
/**
 * Decrypt on-chain storage values via the orchestrator (used when
 * AES_CBC_ENABLED=true — the AES key/IV live only on the server).
 * Returns one entry per input; `null` for items the server could not decrypt.
 */
export async function avalancheServerDecrypt(items) {
    if (items.length === 0)
        return [];
    const address = authSigner?.getAddress() ?? null;
    if (!authSigner || !address) {
        throw new Error("Avalanche decrypt failed: no connected wallet to authorize the request");
    }
    const timestamp = Date.now();
    const message = buildDecryptAuthMessage(address, timestamp, items);
    const signature = await authSigner.signMessage(message, address);
    const res = await fetch(`${getOrchestratorBaseUrl()}/avalanche/decrypt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, auth: { address, timestamp, signature } }),
    });
    if (!res.ok) {
        let reason = `HTTP ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error)
                reason = body.error;
        }
        catch {
            /* ignore body parse errors */
        }
        throw new Error(`Avalanche decrypt failed: ${reason}`);
    }
    const body = (await res.json());
    if (!Array.isArray(body.values) || body.values.length !== items.length) {
        throw new Error("Avalanche decrypt failed: malformed server response");
    }
    return body.values;
}
